import { type Event, type Property, type User } from '@kalos/kalos-rpc';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@kalos/ui';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { useMobileDevice } from '../../../../hooks/useMedia';
import { JobCreationFlow, type JobType } from '../../JobCreation';

export interface UnifiedJobCreateDialogProps {
  open: boolean;
  onClose: () => void;
  initialCustomer?: User | null;
  initialProperty?: Property | null;
}

export const UnifiedJobCreateDialog = ({
  open,
  onClose,
  initialCustomer,
  initialProperty,
}: UnifiedJobCreateDialogProps) => {
  const router = useRouter();
  const isMobileDevice = useMobileDevice();
  const [jobType, setJobType] = useState<JobType | null>(null);

  const handleJobCreated = useCallback(
    (event: Partial<Event>) => {
      if (event.id) {
        // Navigate to the appropriate page based on whether it's a project or service call
        if (event.isProject) {
          router.navigate({
            to: '/project/$projectId',
            params: { projectId: event.id },
          });
        } else {
          router.navigate({
            to: '/jobs/$jobId',
            params: { jobId: event.id },
          });
        }
      }
    },
    [router],
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        // Full screen on phones, where a 90%-high dialog leaves a sliver of page behind it.
        className={
          isMobileDevice
            ? 'theme-light h-svh max-h-svh w-screen max-w-none overflow-y-auto overflow-x-hidden rounded-none bg-white'
            : 'theme-light max-h-[90vh] max-w-6xl overflow-y-auto bg-white'
        }
        disableOutsideClick
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {jobType === 'project' ? 'Create New Project' : 'Create New Job'}
          </DialogTitle>
        </DialogHeader>

        <JobCreationFlow
          onSubmit={handleJobCreated}
          onCancel={onClose}
          showTypeSelection={true}
          showSchedulingBanner={false}
          onJobTypeChange={setJobType}
          initialCustomer={initialCustomer}
          initialProperty={initialProperty}
        />
      </DialogContent>
    </Dialog>
  );
};
